import {
    Avatar,
    ButtonGradientPrimary,
    ButtonSecondary,
    Card,
    Colors,
    Heading,
    Icon,
    ModalDialog,
    Pagination,
    Pills,
    SearchBar,
    Separator,
    Text,
} from '@amelio/ui-library';
import { useState, useMemo, useEffect } from 'react';

import type { User } from '@/types/directory/User';

import { useGetCompanyUsers } from '@/api/directory';
import { MAX_NODES } from '@/components/ReviewerNodes/ReviewerNodes';
import { UserCard } from '@/components/UserCard/UserCard';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useDebounce } from '@/hooks/useDebounce';
import { showWarningToast } from '@/plugins/Toast';
import { getFullName, getProfilePictureUrl } from '@/utils/user';

interface AddReviewerParams {
    revieweeId: string;
    reviewers: User[];
}

const PAGE_SIZE = 6;

interface ReviewerSectionHeaderProps {
    title: string;
}
const ReviewerSectionHeader = ({ title }: ReviewerSectionHeaderProps) => (
    <div className={'flex flex-row items-center'}>
        <div
            className={'bg-secondary-50 mr-4 flex w-fit items-center justify-center rounded-md p-4'}
        >
            <Icon
                name={'users-user'}
                color={Colors.SECONDARY}
                size={40}
                strokeWidth={1.5}
            />
        </div>

        <Heading level={4}>{title}</Heading>
    </div>
);

interface ReviewerCardProps {
    reviewer: User;
    borderColor: string;
    interactive?: boolean;
    disabled?: boolean;
    onClick?: () => void;
}

const ReviewerCard = ({
    reviewer,
    borderColor,
    interactive,
    disabled,
    onClick,
}: ReviewerCardProps) => {
    const { getJobTitle } = useCompanyInformation();

    return (
        <Card
            className={disabled ? 'opacity-50' : ''}
            variant={'elevated'}
            onClick={onClick}
            interactive={interactive}
            borderColor={borderColor}
            padding={'sm'}
        >
            <UserCard
                className={interactive ? 'cursor-pointer' : ''}
                alt={'user-card'}
                avatar={
                    <Avatar
                        alt={getFullName(reviewer)}
                        src={getProfilePictureUrl(reviewer?.profilePictureUrl || '')}
                    />
                }
                description={
                    reviewer.jobTitleId && (
                        <Pills
                            className={'mt-3 text-center'}
                            size={'sm'}
                            variant={'purple'}
                        >
                            {getJobTitle(reviewer.jobTitleId)}
                        </Pills>
                    )
                }
                name={getFullName(reviewer)}
            />
        </Card>
    );
};

interface AssignReviewerModalProps {
    isOpen: boolean;
    reviewee: User;
    reviewers: User[];
    isSaving?: boolean;
    onAddReviewer: (params: AddReviewerParams) => void;
    onClose: () => void;
}

export const AssignReviewerModal = ({
    isOpen,
    reviewee,
    reviewers,
    isSaving,
    onAddReviewer,
    onClose,
}: AssignReviewerModalProps) => {
    const { t } = useAmelioTranslations();

    const [selectedReviewers, setSelectedReviewers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCurrentPage, setSelectedCurrentPage] = useState(1);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // A 360 respondent can be any colleague in the company, so the search is company-wide
    // rather than narrowed to the manager's own reports by the results permission.
    const { data: usersResponse, isFetching: isLoadingUsers } = useGetCompanyUsers({
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
        searchQuery:
            debouncedSearchTerm.trim().length >= 2 ? debouncedSearchTerm.trim() : undefined,
        isActiveOnly: true,
        includeInvited: true,
    });

    const excludedIds = useMemo(() => {
        const ids = new Set<string>();
        ids.add(reviewee.id);
        reviewers.forEach((r) => ids.add(r.id));
        return ids;
    }, [reviewee.id, reviewers]);

    const visibleReviewers = useMemo(() => usersResponse?.items ?? [], [usersResponse?.items]);

    const isExcluded = (user: User) => excludedIds.has(user.id);

    const totalCount = usersResponse?.totalCount ?? 0;
    const totalPages = usersResponse
        ? Math.ceil(usersResponse.totalCount / usersResponse.pageSize)
        : 0;

    const totalSelectedCount = reviewers.length;
    const totalSelectedPages = Math.ceil(totalSelectedCount / PAGE_SIZE);
    const visibleSelectedReviewers = reviewers.slice(
        (selectedCurrentPage - 1) * PAGE_SIZE,
        selectedCurrentPage * PAGE_SIZE,
    );

    // Every opening starts from the reviewers already assigned, with an empty search.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelectedReviewers(reviewers);
        setSearchTerm('');
    }, [isOpen, reviewee.id]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        setSelectedCurrentPage(1);
    }, [reviewers.length]);

    const isSelected = (reviewer: User) => {
        return selectedReviewers.some((selectedReviewer) => selectedReviewer.id === reviewer.id);
    };

    const handleReviewerClick = (reviewer: User) => {
        if (isSelected(reviewer)) {
            setSelectedReviewers((prev) =>
                prev.filter((selectedReviewer) => selectedReviewer.id !== reviewer.id),
            );
            return;
        }

        if (selectedReviewers.length >= MAX_NODES) {
            showWarningToast({
                title: t('evaluation360AssignReviewers.validation.title'),
                message: t('evaluation360AssignReviewers.validation.maxReviewers', {
                    count: MAX_NODES,
                }),
            });
            return;
        }

        setSelectedReviewers((prev) => {
            const updatedReviewers = [...prev, reviewer];

            if (updatedReviewers.length === MAX_NODES) {
                showWarningToast({
                    title: t('evaluation360AssignReviewers.validation.title'),
                    message: t('evaluation360AssignReviewers.validation.maxReviewers', {
                        count: MAX_NODES,
                    }),
                });
            }

            return updatedReviewers;
        });
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
    };

    const handleAddReviewer = () => {
        if (selectedReviewers.length < 1) {
            return;
        }

        onAddReviewer({ revieweeId: reviewee.id, reviewers: selectedReviewers });
        setSelectedReviewers([]);
        onClose();
    };

    const handleClose = () => {
        setSelectedReviewers([]);
        onClose();
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={handleClose}
            title={t('evaluation360AssignReviewers.assignReviewers.addReviewer')}
            titleIcon={
                <Icon
                    name={'users-user'}
                    color={Colors.SECONDARY}
                    ariaHidden
                />
            }
            size={'lg'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            // The reviewer cards are white: on the tinted panel they stand apart, on a white one they vanish.
            className={'bg-gradient-modal-panel'}
            dataTestId={'assign-reviewer-modal'}
            headerExtra={
                <SearchBar
                    size={'sm'}
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder={t('evaluation360AssignReviewers.assignReviewers.searchReviewers')}
                    ariaLabel={t('evaluation360AssignReviewers.assignReviewers.searchReviewers')}
                />
            }
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        onClick={handleClose}
                        dataTestId={'assign-reviewer-cancel'}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        disabled={selectedReviewers.length < 1 || isSaving}
                        onClick={handleAddReviewer}
                        dataTestId={'assign-reviewer-add'}
                    >
                        {t('common.add')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-6'}>
                <div
                    data-testid={'reviewer-selection-container'}
                    className={'flex flex-col gap-5'}
                >
                    <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:!grid-cols-3'}>
                        {visibleReviewers.map((reviewer) => {
                            const excluded = isExcluded(reviewer);
                            return (
                                <ReviewerCard
                                    key={reviewer.id}
                                    reviewer={reviewer}
                                    borderColor={
                                        excluded
                                            ? Colors.WHITE
                                            : isSelected(reviewer)
                                              ? Colors.PRIMARY
                                              : Colors.WHITE
                                    }
                                    interactive={!excluded}
                                    disabled={excluded}
                                    onClick={
                                        excluded ? undefined : () => handleReviewerClick(reviewer)
                                    }
                                />
                            );
                        })}

                        {!isLoadingUsers && visibleReviewers.length < 1 && (
                            <Text>
                                {t('evaluation360AssignReviewers.assignReviewers.noReviewersFound')}
                            </Text>
                        )}
                    </div>

                    <Pagination
                        variant={'rounded'}
                        showResultsSummary={true}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        pageSize={PAGE_SIZE}
                        onPageChange={setCurrentPage}
                        isLoading={isLoadingUsers}
                        dataTestId={'assign-reviewer-pagination'}
                        resultsSummaryText={t('common.showingResults', {
                            start: (currentPage - 1) * PAGE_SIZE + 1,
                            end: Math.min(currentPage * PAGE_SIZE, totalCount),
                            total: totalCount,
                        })}
                    />
                </div>

                <Separator thickness={'thin'} />

                <div className={'flex flex-col gap-5'}>
                    <ReviewerSectionHeader
                        title={t(
                            'evaluation360AssignReviewers.assignReviewers.reviewerAlreadySelected',
                        )}
                    />

                    <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:!grid-cols-3'}>
                        {visibleSelectedReviewers.length > 0 &&
                            visibleSelectedReviewers.map((reviewer) => (
                                <ReviewerCard
                                    key={reviewer.id}
                                    reviewer={reviewer}
                                    borderColor={Colors.PRIMARY}
                                />
                            ))}

                        {reviewers.length < 1 && (
                            <Text>
                                {t(
                                    'evaluation360AssignReviewers.assignReviewers.noReviewersAssigned',
                                )}
                            </Text>
                        )}
                    </div>

                    {totalSelectedCount > PAGE_SIZE && (
                        <Pagination
                            variant={'rounded'}
                            showResultsSummary={true}
                            currentPage={selectedCurrentPage}
                            totalPages={totalSelectedPages}
                            totalCount={totalSelectedCount}
                            pageSize={PAGE_SIZE}
                            onPageChange={setSelectedCurrentPage}
                            dataTestId={'assigned-reviewer-pagination'}
                            resultsSummaryText={t('common.showingResults', {
                                start: (selectedCurrentPage - 1) * PAGE_SIZE + 1,
                                end: Math.min(selectedCurrentPage * PAGE_SIZE, totalSelectedCount),
                                total: totalSelectedCount,
                            })}
                        />
                    )}
                </div>
            </div>
        </ModalDialog>
    );
};
